import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { message, Spin } from "antd";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { globalModel } from "../../global/GlobalModel";
import { SpaceModel } from "../SpaceModel";
import type { MembershipProduct } from "../SpaceType";
import { membershipCanEnterRetailPurchase } from "../membershipPurchaseUtils";
import { membershipNoRetailPurchaseHint } from "../spacePageUtils";
import { resolveProductPosterUrl } from "../product/spaceProductUtils";
import { H5BackButton } from "../../components/common/H5BackButton";
import { h5BackButtonCssVars } from "../../components/common/H5BackButtonConfig";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { PAGE_SHELL_BG } from "../../utils/pageShellBg";
import { usePageShellBg } from "../../utils/usePageShellBg";
import { SpaceProductPurchaseSheet } from "./SpaceProductPurchaseSheet";
import "./SpaceProductSubscribeStyle.css";

function findProduct(products: MembershipProduct[], spaceProductId: string): MembershipProduct | null {
  return products.find((p) => p.spaceProductId === spaceProductId) ?? null;
}

export const SpaceProductSubscribePage = observer(() => {
  const { spaceId, spaceProductId } = useParams<{
    spaceId: string;
    spaceProductId: string;
  }>();
  const navigate = useNavigate();
  const model = useMemo(() => new SpaceModel(), []);
  const [sheetOpen, setSheetOpen] = useState(false);

  usePageShellBg({
    bg: PAGE_SHELL_BG.spaceLight,
    front: "#000000",
    title: "订阅",
  });

  useEffect(() => {
    if (!spaceId || !/^\d{8}$/.test(spaceId)) {
      return;
    }
    model.setSpaceId(spaceId);
    void model.loadConfig();
  }, [spaceId, model]);

  useEffect(() => {
    if (!model.loaded || !globalModel.isLoggedIn) {
      return;
    }
    void model.refreshSubscriptionMerge();
  }, [model, model.loaded, globalModel.isLoggedIn]);

  const product = spaceProductId
    ? findProduct(model.membershipProducts, spaceProductId)
    : null;

  const onBack = useCallback(() => {
    if (spaceId) {
      h5NavigateTo(navigate, `/space/${spaceId}/home`);
    } else {
      navigate(-1);
    }
  }, [navigate, spaceId]);

  const onLoginRequest = useCallback(() => {
    if (!spaceId) {
      return;
    }
    const redirect = encodeURIComponent(`/space/${spaceId}/subscribe/${spaceProductId ?? ""}`);
    h5NavigateTo(navigate, `/login?redirect=${redirect}`);
  }, [navigate, spaceId, spaceProductId]);

  const onSubscribeClick = useCallback(() => {
    if (!product) {
      return;
    }
    if (!globalModel.isLoggedIn) {
      onLoginRequest();
      return;
    }
    const upgradeEligible =
      product.upgradeEligible === true && !!product.upgradeSourceSpaceProductId?.trim();
    if (upgradeEligible) {
      message.info("升级功能移动端接入中");
      return;
    }
    if (!membershipCanEnterRetailPurchase(product)) {
      message.info(membershipNoRetailPurchaseHint(product));
      return;
    }
    setSheetOpen(true);
  }, [onLoginRequest, product]);

  if (!spaceId || !/^\d{8}$/.test(spaceId) || !spaceProductId) {
    return <Navigate to="/" replace />;
  }

  if (!model.loaded) {
    return (
      <div className="space-subscribe-page space-subscribe-page--loading">
        <Spin />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="space-subscribe-page" style={h5BackButtonCssVars()}>
        <header className="space-subscribe-page__header">
          <H5BackButton
            iconClassName="h5-back-button__icon--on-dark"
            onClick={onBack}
          />
        </header>
        <p className="space-subscribe-page__empty">产品不存在或暂不可访问</p>
      </div>
    );
  }

  const posterSrc = resolveProductPosterUrl(product);

  return (
    <div className="space-subscribe-page" style={h5BackButtonCssVars()}>
      <header className="space-subscribe-page__header">
        <H5BackButton
          iconClassName="h5-back-button__icon--on-dark"
          onClick={onBack}
        />
        <span className="space-subscribe-page__header-title">{product.displayName}</span>
        <span className="space-subscribe-page__header-spacer" aria-hidden />
      </header>

      <div className="space-subscribe-page__poster-wrap">
        {posterSrc ? (
          <img src={posterSrc} alt="" className="space-subscribe-page__poster" />
        ) : (
          <div className="space-subscribe-page__poster-fallback">
            <p className="space-subscribe-page__poster-name">{product.displayName}</p>
            {product.description?.trim() ? (
              <p className="space-subscribe-page__poster-desc">{product.description.trim()}</p>
            ) : null}
          </div>
        )}
      </div>

      <div className="space-subscribe-page__foot">
        <button type="button" className="space-subscribe-page__cta" onClick={onSubscribeClick}>
          立即订阅
        </button>
      </div>

      <SpaceProductPurchaseSheet
        spaceId={spaceId}
        row={product}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        isLoggedIn={globalModel.isLoggedIn}
        onLoginRequest={onLoginRequest}
      />
    </div>
  );
});

export default SpaceProductSubscribePage;
